/**
 * Nmbrs Leave Balance Card
 *
 * Verlofsaldi per verloftype, voor één medewerker of voor iedereen binnen het
 * bereik van de ingelogde gebruiker. Story 3.1: Verlofsaldo Sync.
 *
 * Sinds 2026-07-29 zonder voortgangsbalken: Nmbrs kent geen jaarrecht, dus er
 * is geen noemer om tegen af te zetten. De oude balk deelde door een veld dat
 * altijd 0 was en kleurde daardoor iedereen permanent rood.
 *
 * Design system colors:
 * - Navy #2F5A7A: headers, primary, saldo
 * - Teal #54949E: secondary info
 * - Red #D64545: errors, negatief saldo, onvolledige totalen
 */

import { useState, useMemo } from 'react';
import { useLeaveBalances, useAllLeaveBalances } from '../hooks/useNmbrsLeave';
import type { NmbrsLeaveBalance } from '../hooks/useNmbrsLeave';
import {
  formatSaldo,
  formatSaldoMetDagen,
  formatJaarruimte,
  formatJaarruimteToelichting,
  formatDagenMetUren,
} from '../../../lib/verlofSaldo';

// ============================================
// Types
// ============================================

interface NmbrsLeaveBalanceCardProps {
  /** Single employee mode: show balances for one employee */
  employeeId?: number;
  /** Batch mode: show balances for everyone within the caller's server-derived scope */
  showAll?: boolean;
}

/**
 * Vaste volgorde van de verloftypes, gelijk aan de Nmbrs-nummering (1..4).
 * Zie `nmbrsLeaveTypeMap.ts` in de backend — die labels zijn leidend.
 */
const TYPE_VOLGORDE = [
  'Wettelijk verlof',
  'Bovenwettelijk verlof',
  'Kort verzuim + Bijz.verlof',
  'TvT uren',
];

// ============================================
// Sub-components
// ============================================

/** Year selector dropdown. */
function YearSelector({
  year,
  onChange,
}: {
  year: number;
  onChange: (year: number) => void;
}) {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - 4 + i);

  return (
    <select
      value={year}
      onChange={(e) => onChange(Number(e.target.value))}
      className="px-3 py-1.5 border border-[#E2E8F0] rounded-lg text-sm text-[#1E293B] focus:ring-2 focus:ring-[#54949E] focus:border-[#54949E]"
    >
      {years.map((y) => (
        <option key={y} value={y}>
          {y}
        </option>
      ))}
    </select>
  );
}

/**
 * Eén verlofregel: het type, wat er nog staat, en wat er is opgenomen.
 *
 * Geen voortgangsbalk en geen percentage (besluit Friso 2026-07-29). Een balk
 * heeft een noemer nodig en die bestaat niet: Nmbrs kent geen jaarrecht, dus
 * elke noemer zou verzonnen zijn. De vorige balk deelde door een veld dat
 * altijd 0 was, waardoor iedereen permanent in het rood stond.
 */
function BalanceRow({ balance }: { balance: NmbrsLeaveBalance }) {
  const isUnknown = balance.remaining === null;
  const isNegative = balance.remaining !== null && balance.remaining < 0;

  // Alleen wettelijk en bovenwettelijk bouwen met de tijd op; daar heeft een
  // jaarprognose betekenis. Bij kort verzuim en TvT blijft het één cijfer.
  const heeftJaarruimte = balance.yearRemaining !== null;

  return (
    <div className="py-3 border-b border-[#E2E8F0] last:border-b-0">
      <div className="text-sm font-medium text-[#1E293B]">{balance.type}</div>

      {heeftJaarruimte ? (
        <>
          <div className="mt-1 text-xs text-gray-500">Nog te plannen dit jaar</div>
          <div
            className="text-2xl font-bold text-[#2F5A7A] leading-tight"
            // Zonder het label te herhalen: dat staat al in de regel erboven en
            // zou anders twee keer voorgelezen worden.
            aria-label={`${formatJaarruimte(
              balance.yearRemaining,
              balance.hoursPerDay,
              balance.varyingSchedule
            )}, ${balance.yearRemaining} uur. Verwachting als je het jaar uitwerkt.`}
          >
            {formatJaarruimte(balance.yearRemaining, balance.hoursPerDay, balance.varyingSchedule)}
          </div>
          <div className="text-xs text-gray-500">
            {formatJaarruimteToelichting(balance.yearRemaining, balance.entitlementBasisDays)}
          </div>

          <div className="mt-2 pt-2 border-t border-[#E2E8F0] flex flex-wrap gap-x-4 text-xs text-gray-600">
            <span>
              Nu opgebouwd:{' '}
              <span className={isUnknown ? 'text-gray-400 italic' : isNegative ? 'text-[#D64545]' : ''}>
                {formatSaldoMetDagen(balance.remaining, balance.hoursPerDay)}
              </span>
            </span>
            <span>Opgenomen dit jaar: {balance.used} uur</span>
            {balance.expired > 0 && <span>Vervallen: {balance.expired} uur</span>}
          </div>

          {isNegative && (
            <p className="mt-1 text-xs text-gray-500">
              Je loopt voor op je opbouw. Dat mag — je bouwt het jaar door verder op.
            </p>
          )}
        </>
      ) : (
        // Geen jaarprognose: één getal, zoals het was.
        <>
          <div className="mt-1 flex items-center justify-between">
            <span className="text-xs text-gray-500">Nu opgebouwd</span>
            <span
              className={`text-sm font-semibold ${
                isUnknown ? 'text-gray-400 italic' : isNegative ? 'text-[#D64545]' : 'text-[#2F5A7A]'
              }`}
            >
              {formatSaldo(balance.remaining)}
            </span>
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-xs text-gray-500">Opgenomen: {balance.used} uur</span>
            {balance.expired > 0 && (
              <span className="text-xs text-gray-500">Vervallen: {balance.expired} uur</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** Loading skeleton for balance cards. */
function BalanceSkeleton() {
  return (
    <div className="animate-pulse space-y-4 p-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="space-y-2">
          <div className="flex justify-between">
            <div className="h-4 bg-gray-200 rounded w-24" />
            <div className="h-4 bg-gray-200 rounded w-20" />
          </div>
          <div className="h-2.5 bg-gray-200 rounded-full w-full" />
          <div className="flex justify-between">
            <div className="h-3 bg-gray-100 rounded w-28" />
            <div className="h-3 bg-gray-100 rounded w-28" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Error state with retry button. */
function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="p-6 text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-50 mb-3">
        <svg className="w-6 h-6 text-[#D64545]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      </div>
      <p className="text-sm text-[#1E293B] mb-3">{message}</p>
      <button
        onClick={onRetry}
        className="px-4 py-2 text-sm font-medium text-white bg-[#2F5A7A] rounded-lg hover:bg-[#254a65] transition-colors"
      >
        Opnieuw proberen
      </button>
    </div>
  );
}

/** Not linked message. */
function NotLinkedMessage() {
  return (
    <div className="p-6 text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-3">
        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
          />
        </svg>
      </div>
      <p className="text-sm text-gray-500">
        Niet gekoppeld aan Nmbrs
      </p>
      <p className="text-xs text-gray-400 mt-1">
        Koppel deze medewerker eerst aan een Nmbrs-account om verlofsaldi te bekijken.
      </p>
    </div>
  );
}

// ============================================
// Single Employee Card
// ============================================

function SingleEmployeeCard({ employeeId, year }: { employeeId: number; year: number }) {
  const { data, isLoading, error, refetch } = useLeaveBalances(employeeId, year);

  if (isLoading) return <BalanceSkeleton />;

  // Check for "not linked" error (match error code or status 404)
  if (error) {
    const errorMessage = error instanceof Error ? error.message : 'Onbekende fout';
    const isNotLinked =
      errorMessage.includes('EMPLOYEE_NOT_LINKED') ||
      errorMessage.includes('not linked') ||
      errorMessage.includes('404');
    if (isNotLinked) {
      return <NotLinkedMessage />;
    }
    return <ErrorState message={errorMessage} onRetry={() => refetch()} />;
  }

  const balances = data?.data ?? [];

  if (balances.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-sm text-gray-500">Geen verlofgegevens gevonden voor {year}.</p>
      </div>
    );
  }

  return (
    <div className="px-4 py-2">
      {balances.map((balance) => (
        <BalanceRow key={`${balance.type}-${balance.year}`} balance={balance} />
      ))}
    </div>
  );
}

// ============================================
// All Employees Card
// ============================================

/**
 * Onthoudt de detail-schakelaar per gebruiker.
 *
 * HR gebruikt dit scherm anders dan een teamleider: wie de verlofsoorten altijd
 * wil zien, moet dat niet elke keer opnieuw hoeven aanzetten. Faalt de opslag
 * (privémodus, geblokkeerde storage), dan valt hij stil terug op de standaard —
 * een voorkeur is het niet waard om een scherm op te laten blazen.
 */
const DETAIL_VOORKEUR_SLEUTEL = 'verlofsaldo:toon-per-verlofsoort';

function leesDetailVoorkeur(): boolean {
  try {
    return window.localStorage.getItem(DETAIL_VOORKEUR_SLEUTEL) === '1';
  } catch {
    return false;
  }
}

function bewaarDetailVoorkeur(aan: boolean): void {
  try {
    window.localStorage.setItem(DETAIL_VOORKEUR_SLEUTEL, aan ? '1' : '0');
  } catch {
    // Bewust stil: de weergave werkt gewoon, alleen niet onthouden.
  }
}

/**
 * Eén schakelaar voor de HELE tabel, niet per rij.
 *
 * Per rij uitklappen breekt waar een teamoverzicht voor is: vergelijken. Klap
 * je één medewerker open, dan kun je diens verlofsoorten met niemand naast
 * elkaar leggen. De detailvraag is "hoe zit de verdeling over mijn team", niet
 * "vertel me over deze persoon".
 */
function DetailToggle({ aan, onChange }: { aan: boolean; onChange: (aan: boolean) => void }) {
  return (
    <button
      type="button"
      data-testid="balances-detail-toggle"
      aria-expanded={aan}
      onClick={() => onChange(!aan)}
      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border border-[#2F5A7A]/30 bg-[#2F5A7A]/5 text-[#2F5A7A] hover:bg-[#2F5A7A]/10"
    >
      {/* Een chevron maakt van een grijs randje een uitklapper. Friso zag de
          vorige versie niet staan — een knop die niemand vindt bestaat niet. */}
      <span aria-hidden="true">{aan ? '▾' : '▸'}</span>
      {aan ? 'Verberg verlofsoorten' : 'Toon per verlofsoort'}
    </button>
  );
}

/** Sorteerkeuze. Bewust PER verloftype, niet over een optelsom van alle types. */
type SortMode = { kind: 'name' } | { kind: 'remaining' | 'used'; type: string };

/**
 * Sommeer over verloftypes, met "onbekend" als besmettelijke waarde.
 *
 * Eén onbekend deel maakt het TOTAAL onbekend — niet stilzwijgend de rest
 * optellen. Een schoon getal dat een ontbrekende post verzwijgt is precies de
 * fout die de saldobug van 2026-07-29 zo lang verborgen hield. De losse
 * kolommen laten wél zien wat er wél bekend is, dus er verdwijnt niets.
 */
function somOfOnbekend(waarden: Array<number | null>): number | null {
  if (waarden.length === 0) return null;
  if (waarden.some((v) => v === null)) return null;
  const totaal = (waarden as number[]).reduce((sum, v) => sum + v, 0);
  return Math.round(totaal * 10) / 10;
}

/**
 * Eén cel: "2 dgn (16 uur)". Dagen voorop omdat je daarin plant, uren erbij
 * omdat de loonstrook en Nmbrs daarin rekenen. Zonder rooster alleen uren —
 * een dagental op een aangenomen dagduur zou voor parttimers fout zijn.
 *
 * "onbekend" grijs, negatief rood.
 */
function UrenCel({
  waarde,
  urenPerDag,
  wisselendRooster = false,
  nadruk = false,
  metUren = false,
}: {
  waarde: number | null;
  urenPerDag: number | null;
  wisselendRooster?: boolean;
  nadruk?: boolean;
  /** Uren tussen haakjes erbij. Alleen in de uitgeklapte weergave. */
  metUren?: boolean;
}) {
  if (waarde === null) {
    return <span className="text-gray-400 italic">onbekend</span>;
  }
  return (
    <span
      // Het voorbehoud bij een wisselend rooster stond als "±" vóór het getal
      // en brak de uitlijning van de kolom. Als tooltip blijft het bewaard.
      title={
        wisselendRooster
          ? 'Bij een wisselend rooster is het dagental een gemiddelde; de uren zijn exact.'
          : undefined
      }
      className={`tabular-nums whitespace-nowrap ${
        waarde < 0 ? 'text-[#D64545]' : nadruk ? 'text-[#2F5A7A] font-semibold' : 'text-[#1E293B]'
      }`}
    >
      {formatDagenMetUren(waarde, urenPerDag, wisselendRooster, metUren, false)}
    </span>
  );
}

/**
 * Eén rij per medewerker.
 *
 * Twee totalen die NIET verward mogen worden:
 * - "Saldo nu" is een momentopname: wat er vandaag op de teller staat.
 * - "Nog te plannen dit jaar" is een vooruitblik: dat saldo plus de opbouw die
 *   dit jaar nog komt, en alleen over vakantieverlof (wettelijk +
 *   bovenwettelijk). Kort verzuim en TvT bouwen niet met de tijd op.
 *
 * Ze staan daarom in aparte kolommen met een eigen kop, en het jaarcijfer
 * krijgt als enige nadruk — dat is het getal waarop een leidinggevende plant.
 */
function EmployeeRow({
  employee,
  types,
  year,
  detail,
}: {
  employee: { employeeId: number; employeeName: string; balances: NmbrsLeaveBalance[] };
  types: string[];
  year: number;
  /** Uitgeklapt: verlofsoorten, "Saldo nu" en de uren erbij. */
  detail: boolean;
}) {
  const perType = new Map(employee.balances.map((b) => [b.type, b]));
  // Dagduur en roosterwisseling zijn eigenschappen van de MEDEWERKER, niet van
  // een verloftype; ze staan alleen per balansregel omdat het object dat vorm
  // heeft. Eén keer uitlezen en op elke cel toepassen.
  const urenPerDag = employee.balances[0]?.hoursPerDay ?? null;
  const wisselend = employee.balances[0]?.varyingSchedule ?? false;

  const saldoNu = somOfOnbekend(types.map((t) => perType.get(t)?.remaining ?? null));

  // Opgenomen en gepland tellen alleen VAKANTIEverlof, net als de jaarruimte.
  // Zou kort verzuim meelopen, dan trok je die uren af van een cijfer dat er
  // niet over gaat en klopte de rekensom op het scherm niet meer.
  const vakantie = employee.balances.filter((b) => b.yearEntitlement !== null);
  const opgenomen = Math.round(vakantie.reduce((sum, b) => sum + b.used, 0) * 10) / 10;
  // Goedgekeurd verlof dat nog vóór ons ligt. Nmbrs heeft dit AL van het saldo
  // afgetrokken (gemeten op productie 2026-07-30: zonder die aftrek zou de
  // opbouw van vier medewerkers negatief moeten zijn, wat niet kan). Het staat
  // hier puur ter informatie en wordt nergens nóg een keer afgetrokken.
  const gepland = Math.round(vakantie.reduce((sum, b) => sum + b.planned, 0) * 10) / 10;
  const beginsaldo = somOfOnbekend(vakantie.map((b) => b.yearStartBalance));

  // Alleen de types die überhaupt een jaarruimte kennen tellen mee.
  const teePlannen = vakantie.length === 0 ? null : somOfOnbekend(vakantie.map((b) => b.yearRemaining));
  const vervallen = Math.round(vakantie.reduce((sum, b) => sum + (b.expired ?? 0), 0) * 10) / 10;

  // Sluit de som? Nmbrs' saldo is niet altijd de som van zijn onderdelen —
  // gemeten op productie klopte het bij 7 van de 8 medewerkers, en week er één
  // 48 uur af. Liever die rij markeren dan getallen tonen die zichtbaar niet
  // optellen. Tolerantie van een uur voor afrondingsruis in de opbouwbreuk.
  const sluitNiet =
    beginsaldo !== null &&
    teePlannen !== null &&
    Math.abs(beginsaldo - opgenomen - gepland - vervallen - teePlannen) > 1;

  if (employee.balances.length === 0) {
    return (
      <tr data-testid="employee-row" className="border-t border-[#E2E8F0]">
        <th scope="row" className="py-2 pr-3 text-left font-medium text-[#2F5A7A] whitespace-nowrap">
          {employee.employeeName}
        </th>
        <td colSpan={(detail ? types.length + 1 : 0) + 3} className="py-2 text-xs text-gray-400">
          Geen verlofgegevens voor {year}
        </td>
      </tr>
    );
  }

  return (
    <tr data-testid="employee-row" className="border-t border-[#E2E8F0]">
      <th
        scope="row"
        className="py-2 pr-3 text-left font-medium text-[#2F5A7A] whitespace-nowrap sticky left-0 bg-white"
      >
        {employee.employeeName}
      </th>
      {detail &&
        types.map((type) => (
          <td key={type} className="py-2 px-3 text-right">
            {perType.has(type) ? (
              <UrenCel
                waarde={perType.get(type)!.remaining}
                urenPerDag={urenPerDag}
                wisselendRooster={wisselend}
                metUren
              />
            ) : (
              <span className="text-gray-300">—</span>
            )}
          </td>
        ))}
      {/* "Saldo nu" is de momentopname en hoort bij het DETAIL. Naast "Nog te
          plannen dit jaar" in de standaardweergave zou het precies de
          verwarring terugbrengen die dat jaarcijfer moest wegnemen: in
          februari leest een lage dagstand als een weigering. */}
      {detail && (
        <td className="py-2 px-3 text-right border-l border-[#E2E8F0]">
          <UrenCel waarde={saldoNu} urenPerDag={urenPerDag} wisselendRooster={wisselend} metUren />
        </td>
      )}
      <td
        className={`py-2 px-3 text-right text-gray-600 ${detail ? '' : 'border-l border-[#E2E8F0]'}`}
      >
        <UrenCel waarde={beginsaldo} urenPerDag={urenPerDag} wisselendRooster={wisselend} metUren={detail} />
      </td>
      <td className="py-2 px-3 text-right text-gray-600">
        <UrenCel
          waarde={opgenomen}
          urenPerDag={urenPerDag}
          wisselendRooster={wisselend}
          metUren={detail}
        />
      </td>
      <td className="py-2 px-3 text-right text-gray-600">
        <UrenCel
          waarde={gepland}
          urenPerDag={urenPerDag}
          wisselendRooster={wisselend}
          metUren={detail}
        />
      </td>
      <td className="py-2 pl-3 text-right">
        <UrenCel
          waarde={teePlannen}
          urenPerDag={urenPerDag}
          wisselendRooster={wisselend}
          nadruk
          metUren={detail}
        />
        {sluitNiet && (
          <span
            data-testid="som-sluit-niet"
            title="Het beginsaldo min opgenomen en gepland komt niet uit op dit getal. Nmbrs' saldo is hier niet de som van zijn onderdelen."
            className="ml-1 text-[#D64545] cursor-help"
          >
            *
          </span>
        )}
      </td>
    </tr>
  );
}



/**
 * Wat een voetregel met kale getallen niet kan zeggen.
 *
 * Vervallen uren en negatieve saldi stonden in het oude totalenblok als vaste
 * regels. Ze zijn te belangrijk om te laten vallen — vervallen uren verklaren
 * waarom opgenomen en resterend niet optellen, en een negatief saldo drukt het
 * teamtotaal zonder dat je ziet wie het veroorzaakt. Maar ze zijn ook meestal
 * nul, dus ze verschijnen alleen wanneer er iets te melden valt.
 */
function TotalsNotities({
  employees,
}: {
  employees: Array<{ employeeId: number; employeeName: string; balances: NmbrsLeaveBalance[] }>;
}) {
  const alle = employees.flatMap((e) => e.balances);
  const vervallen = Math.round(alle.reduce((sum, b) => sum + (b.expired ?? 0), 0) * 10) / 10;
  const negatief = employees.filter((e) =>
    e.balances.some((b) => b.remaining !== null && b.remaining < 0)
  ).length;

  if (vervallen === 0 && negatief === 0) return null;

  return (
    <div data-testid="totals-notities" className="mt-2 space-y-0.5 text-xs text-gray-600">
      {vervallen !== 0 && (
        <p>
          Vervallen dit jaar: <strong>{vervallen}</strong> uur — al van de saldi af, dus opgenomen
          en resterend tellen niet op tot het jaarrecht.
        </p>
      )}
      {negatief > 0 && (
        <p className="text-[#D64545]">
          {negatief === 1 ? '1 medewerker staat' : `${negatief} medewerkers staan`} negatief op een
          verlofsoort — dat drukt het teamtotaal.
        </p>
      )}
    </div>
  );
}

/**
 * Optellen OVER MEDEWERKERS heen, met een andere onbekend-regel dan binnen één
 * rij — en dat is opzet.
 *
 * Binnen een rij bestaat een totaal uit vier verlofsoorten; valt er één weg,
 * dan verandert het antwoord wezenlijk, dus wordt het geheel "onbekend".
 * Over een team van twintig mensen is dat juist onbruikbaar: één mislukte
 * Nmbrs-call zou het hele teamcijfer wissen. Hier tellen we daarom wél op wat
 * bekend is en zeggen erbij hoeveel mensen ontbreken.
 */
function somOverMedewerkers(waarden: Array<number | null>): {
  totaal: number | null;
  onbekend: number;
} {
  const bekend = waarden.filter((v): v is number => v !== null);
  return {
    totaal: bekend.length === 0 ? null : Math.round(bekend.reduce((a, b) => a + b, 0) * 10) / 10,
    onbekend: waarden.length - bekend.length,
  };
}

/**
 * Teamtotalen als voetregel van dezelfde tabel.
 *
 * Stond eerder in een apart blok bóven de tabel: dezelfde gegevens langs een
 * andere as, met eigen koppen en markeringen. Onderaan de kolom die ze optellen
 * staan ze op dezelfde plek als de getallen erboven, dus je hoeft niets te
 * vertalen om te vergelijken.
 */
function TeamTotalsRow({
  employees,
  types,
  detail,
  employeeCount,
}: {
  employees: Array<{ employeeId: number; employeeName: string; balances: NmbrsLeaveBalance[] }>;
  types: string[];
  detail: boolean;
  employeeCount: number;
}) {
  const alle = employees.flatMap((e) => e.balances);
  const urenPerDag = employees[0]?.balances[0]?.hoursPerDay ?? null;

  const perType = (type: string) =>
    somOverMedewerkers(alle.filter((b) => b.type === type).map((b) => b.remaining));

  const saldoNu = somOverMedewerkers(alle.map((b) => b.remaining));
  // Dezelfde vakantie-scope als in een medewerkersrij, anders telt de voetregel
  // iets anders op dan de kolom erboven.
  const vakantie = alle.filter((b) => b.yearEntitlement !== null);
  const beginsaldo = somOverMedewerkers(vakantie.map((b) => b.yearStartBalance));
  const opgenomen = Math.round(vakantie.reduce((sum, b) => sum + b.used, 0) * 10) / 10;
  const gepland = Math.round(vakantie.reduce((sum, b) => sum + b.planned, 0) * 10) / 10;
  const teePlannen = somOverMedewerkers(vakantie.map((b) => b.yearRemaining));

  return (
    <tr data-testid="totals-row" className="border-t-2 border-[#E2E8F0] font-medium">
      <th scope="row" className="py-2 pr-3 text-left text-[#1E293B] whitespace-nowrap sticky left-0 bg-white">
        Team ({employeeCount})
      </th>
      {detail &&
        types.map((type) => (
          <td key={type} data-testid={`totals-${type}`} className="py-2 px-3 text-right">
            <UrenCel waarde={perType(type).totaal} urenPerDag={urenPerDag} metUren />
          </td>
        ))}
      {detail && (
        <td className="py-2 px-3 text-right border-l border-[#E2E8F0]">
          <UrenCel waarde={saldoNu.totaal} urenPerDag={urenPerDag} metUren />
        </td>
      )}
      {/* Deze cel ontbrak, waardoor de hele voetregel een kolom opschoof en
          het jaartotaal buiten de tabel viel. */}
      <td className={`py-2 px-3 text-right ${detail ? '' : 'border-l border-[#E2E8F0]'}`}>
        <UrenCel waarde={beginsaldo.totaal} urenPerDag={urenPerDag} metUren={detail} />
      </td>
      <td className="py-2 px-3 text-right">
        <UrenCel waarde={opgenomen} urenPerDag={urenPerDag} metUren={detail} />
      </td>
      <td className="py-2 px-3 text-right">
        <UrenCel waarde={gepland} urenPerDag={urenPerDag} metUren={detail} />
      </td>
      <td className="py-2 pl-3 text-right">
        <UrenCel waarde={teePlannen.totaal} urenPerDag={urenPerDag} nadruk metUren={detail} />
      </td>
    </tr>
  );
}

/**
 * Het teamoverzicht als tabel: één rij per medewerker, zodat je mensen naast
 * elkaar kunt leggen. De vorige weergave stapelde vier blokken per persoon
 * onder elkaar, waardoor vergelijken niet lukte (feedback Friso 2026-07-30).
 *
 * Bij smalle schermen schuift de tabel horizontaal en blijft de naamkolom
 * staan — één rij per medewerker blijft dan overeind, wat de hele bedoeling is.
 */
function TeamBalanceTable({
  employees,
  types,
  year,
  detail,
  employeeCount,
}: {
  employees: Array<{ employeeId: number; employeeName: string; balances: NmbrsLeaveBalance[] }>;
  types: string[];
  year: number;
  detail: boolean;
  /** Inclusief medewerkers zonder gegevens — de voetregel benoemt de omvang. */
  employeeCount: number;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="balances-table">
        <thead>
          <tr className="text-xs text-gray-500">
            <th scope="col" className="py-2 pr-3 text-left font-medium sticky left-0 bg-white">
              Medewerker
            </th>
            {detail &&
              types.map((type) => (
                <th key={type} scope="col" className="py-2 px-3 text-right font-medium whitespace-nowrap">
                  {type}
                </th>
              ))}
            {detail && (
              <th scope="col" className="py-2 px-3 text-right font-medium border-l border-[#E2E8F0] whitespace-nowrap">
                Saldo nu
              </th>
            )}
            <th
              scope="col"
              className={`py-2 px-3 text-right font-medium whitespace-nowrap ${
                detail ? '' : 'border-l border-[#E2E8F0]'
              }`}
            >
              Beginsaldo (dgn)
              <span className="block font-normal text-gray-400">jaarrecht + meegenomen</span>
            </th>
            <th scope="col" className="py-2 px-3 text-right font-medium whitespace-nowrap">
              Opgenomen (dgn)
            </th>
            <th scope="col" className="py-2 px-3 text-right font-medium whitespace-nowrap">
              Gepland (dgn)
              {/* Al van het saldo af bij Nmbrs — dus NIET nog eens aftrekken van
                  "Nog te plannen dit jaar". Dat staat er zo bij, omdat de
                  natuurlijke aanname het tegenovergestelde is. */}
              <span className="block font-normal text-gray-400">al verrekend</span>
            </th>
            <th scope="col" className="py-2 pl-3 text-right font-medium whitespace-nowrap">
              Nog te plannen dit jaar (dgn)
              {/* De rekenbasis erbij: één organisatiebrede instelling klopt niet
                  voor iedereen, dus de lezer moet kunnen herkennen dat het
                  cijfer niet op hem slaat. */}
              <span className="block font-normal text-gray-400">beginsaldo − opgenomen − gepland</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {employees.map((emp) => (
            <EmployeeRow key={emp.employeeId} employee={emp} types={types} year={year} detail={detail} />
          ))}
        </tbody>
        <tfoot>
          <TeamTotalsRow
            employees={employees}
            types={types}
            detail={detail}
            employeeCount={employeeCount}
          />
        </tfoot>
      </table>
    </div>
  );
}

function AllEmployeesCard({ year }: { year: number }) {
  const { data, isLoading, error, refetch } = useAllLeaveBalances(year);
  // Sorteerkeuze staat BOVEN de jaar-afhankelijke query, zodat hij een
  // jaarwissel of refetch overleeft in plaats van terug te vallen op naam.
  const [sort, setSort] = useState<SortMode>({ kind: 'name' });
  const [detail, setDetail] = useState<boolean>(leesDetailVoorkeur);

  function wisselDetail(aan: boolean) {
    setDetail(aan);
    bewaarDetailVoorkeur(aan);
  }

  const employees = data?.data ?? [];
  const errors = data?.errors ?? [];
  const unlinkedCount = data?.unlinkedCount ?? 0;
  // Scope komt van de SERVER, niet van een prop: op /team kan ook een
  // HR-gebruiker binnenlopen, en die krijgt de hele organisatie terug.
  const scopeLabel = data?.scope ?? 'org';

  const presentTypes = useMemo(() => {
    const seen = new Set<string>();
    for (const emp of employees) for (const b of emp.balances) seen.add(b.type);
    // Op verlofsoort, niet op alfabet. Alfabetisch belandt "Wettelijk verlof"
    // achteraan terwijl dat het type is waar de meeste vakantie vandaan komt;
    // een onbekend type valt terug op alfabetische volgorde achteraan.
    return [...seen].sort((a, b) => {
      const ia = TYPE_VOLGORDE.indexOf(a);
      const ib = TYPE_VOLGORDE.indexOf(b);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return a.localeCompare(b, 'nl');
    });
  }, [employees]);

  // Valt terug op naam zodra het gekozen verloftype uit beeld verdwijnt (na
  // een jaarwissel bijvoorbeeld). Zonder dit toont de keuzelijst "Op naam"
  // terwijl de state nog een verdwenen type vasthoudt: de lijst sorteert dan
  // stilletjes op naam en de gebruiker kan die stand niet meer terugkiezen.
  const effectiveSort: SortMode =
    sort.kind !== 'name' && !presentTypes.includes(sort.type) ? { kind: 'name' } : sort;

  const sorted = useMemo(() => {
    const rows = [...employees];
    if (effectiveSort.kind === 'name') {
      return rows.sort((a, b) => a.employeeName.localeCompare(b.employeeName, 'nl') || a.employeeId - b.employeeId);
    }
    const valueOf = (emp: typeof rows[number]) => {
      const b = emp.balances.find((x) => x.type === effectiveSort.type);
      if (!b) return null;
      // Een onbekend saldo levert net als een ontbrekend verloftype `null` op:
      // beide betekenen "geen getal om op te rangschikken". Ze zakken samen
      // naar onderen in plaats van als 0 tussen de echte saldi te belanden.
      return effectiveSort.kind === 'remaining' ? b.remaining : b.used;
    };
    return rows.sort((a, b) => {
      const va = valueOf(a);
      const vb = valueOf(b);
      // Wie dit verloftype niet heeft zakt naar onderen — anders zou "geen
      // gegevens" als een saldo van 0 meedoen in de rangschikking.
      if (va === null && vb === null) return a.employeeName.localeCompare(b.employeeName, 'nl') || a.employeeId - b.employeeId;
      if (va === null) return 1;
      if (vb === null) return -1;
      return vb - va || a.employeeName.localeCompare(b.employeeName, 'nl') || a.employeeId - b.employeeId;
    });
  }, [employees, effectiveSort]);

  if (isLoading) return <BalanceSkeleton />;

  if (error) {
    const errorMessage = error instanceof Error ? error.message : 'Onbekende fout';
    return <ErrorState message={errorMessage} onRetry={() => refetch()} />;
  }

  // Drie verschillende oorzaken van "niets te zien", drie verschillende
  // uitleggen. De oude tekst ("koppel eerst medewerkers aan Nmbrs") is een
  // HR-actie en zegt een leidinggevende niets.
  if (employees.length === 0 && errors.length === 0) {
    return (
      <div className="p-6 text-center" data-testid="balances-empty">
        <p className="text-sm text-gray-500">
          {scopeLabel === 'team'
            ? unlinkedCount > 0
              ? `Geen verlofgegevens: ${unlinkedCount === 1 ? 'je teamlid is' : `je ${unlinkedCount} teamleden zijn`} niet gekoppeld aan Nmbrs.`
              : 'Je hebt (nog) geen teamleden met verlofgegevens.'
            : 'Geen gekoppelde medewerkers gevonden. Koppel eerst medewerkers aan Nmbrs.'}
        </p>
      </div>
    );
  }

  const allFailed = employees.length === 0 && errors.length > 0;
  const noDataAtAll = employees.length > 0 && employees.every((e) => e.balances.length === 0);
  const incompleteCount = errors.length + unlinkedCount;

  return (
    <div className="divide-y divide-[#E2E8F0]">
      {/* Alleen nog de bedieningsstrook. De teamtotalen die hier stonden zijn
          de voetregel van de tabel geworden: onderaan de kolom die ze optellen,
          op dezelfde positie als de getallen erboven. */}
      {presentTypes.length > 0 && (
        <div className="p-4 pb-0" data-testid="balances-totals">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-[#2F5A7A]">Verlofsaldi {year}</h4>
            <div className="flex items-center gap-2">
              <DetailToggle aan={detail} onChange={wisselDetail} />
              <SortSelector types={presentTypes} sort={effectiveSort} onChange={setSort} />
            </div>
          </div>
        </div>
      )}

      {allFailed && (
        <div className="p-4" data-testid="balances-all-failed">
          <p className="text-sm text-[#D64545]">
            Geen enkel verlofsaldo kon worden opgehaald. Er wordt bewust geen totaal getoond — dat zou 0 uur lijken.
          </p>
        </div>
      )}

      {noDataAtAll && (
        <div className="p-4" data-testid="balances-no-year-data">
          <p className="text-sm text-gray-500">Geen verlofgegevens voor {year}.</p>
        </div>
      )}

      {sorted.length > 0 && presentTypes.length > 0 && (
        <div className="p-4">
          {/* Eén keer de eenheid noemen scheelt hem in elke cel te herhalen —
              dat was de grootste bron van drukte. */}
          {/* De uitleg zelf is ook de schakelaar. De knop rechtsboven stond
              naast de sorteerkeuze en werd daar over het hoofd gezien; hier
              leest iemand toch al wat er te zien valt. */}
          <p className="mb-1 text-xs text-gray-400">
            {detail ? 'In dagen, met de exacte uren erachter. ' : 'In dagen. '}
            <button
              type="button"
              data-testid="balances-detail-hint"
              onClick={() => wisselDetail(!detail)}
              className="underline underline-offset-2 text-[#54949E] hover:text-[#2F5A7A]"
            >
              {detail ? 'Verberg de verlofsoorten' : 'Klap uit voor de verlofsoorten en de uren'}
            </button>
          </p>
          <TeamBalanceTable
            employees={sorted}
            types={presentTypes}
            year={year}
            detail={detail}
            employeeCount={employees.length + errors.length + unlinkedCount}
          />
          {/* Uitzonderingsregels: alleen zichtbaar als er iets te melden is.
              Het oude totalenblok droeg deze signalen in vier vaste regels;
              zo blijven ze bestaan zonder permanent ruimte te kosten. */}
          <TotalsNotities employees={sorted} />
          {incompleteCount > 0 && (
            <p data-testid="totals-incomplete" className="mt-2 text-xs text-[#D64545]">
              Onvolledig: {incompleteCount === 1 ? '1 medewerker ontbreekt' : `${incompleteCount} medewerkers ontbreken`} in dit totaal
              {errors.length > 0 && unlinkedCount > 0
                ? ` (${errors.length} met een ophaalfout, ${unlinkedCount} zonder Nmbrs-koppeling)`
                : errors.length > 0
                  ? ' (ophaalfout)'
                  : ' (geen Nmbrs-koppeling)'}.
            </p>
          )}
        </div>
      )}

      {errors.length > 0 && (
        <div className="p-4">
          <h4 className="text-sm font-semibold text-[#D64545] mb-2">
            Fouten bij ophalen ({errors.length})
          </h4>
          {errors.map((err) => (
            <div key={err.employeeId} className="text-xs text-gray-500 py-1">
              {err.employeeName}: {err.error}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Sorteerkeuze: naam, of per verloftype op resterend/opgenomen. */
function SortSelector({
  types,
  sort,
  onChange,
}: {
  types: string[];
  sort: SortMode;
  onChange: (mode: SortMode) => void;
}) {
  const value = sort.kind === 'name' ? 'name' : `${sort.kind}::${sort.type}`;
  return (
    <select
      data-testid="balances-sort"
      aria-label="Sorteren"
      value={value}
      onChange={(e) => {
        const v = e.target.value;
        if (v === 'name') return onChange({ kind: 'name' });
        const [kind, type] = v.split('::');
        onChange({ kind: kind as 'remaining' | 'used', type: type ?? '' });
      }}
      className="px-2 py-1 text-xs border border-[#E2E8F0] rounded-md text-[#1E293B]"
    >
      <option value="name">Op naam</option>
      {types.map((t) => (
        <option key={`r-${t}`} value={`remaining::${t}`}>Meeste resterend — {t}</option>
      ))}
      {types.map((t) => (
        <option key={`u-${t}`} value={`used::${t}`}>Meeste opgenomen — {t}</option>
      ))}
    </select>
  );
}

// ============================================
// Main Component
// ============================================

export function NmbrsLeaveBalanceCard({ employeeId, showAll }: NmbrsLeaveBalanceCardProps) {
  const [year, setYear] = useState(new Date().getFullYear());

  return (
    <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[#E2E8F0] flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-[#2F5A7A]">Verlofsaldi</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {showAll ? 'Verlofsaldi binnen je bereik' : 'Verlofoverzicht vanuit Nmbrs'}
          </p>
          {/* De jaarkiezer stuurt alleen de OPNAMEcijfers aan. Nmbrs' saldo-call
              kent geen jaar — je krijgt altijd de stand van vandaag terug. Dat
              hier zeggen is eerlijker dan een jaartal boven een getal zetten
              dat er niet bij hoort. */}
          <p className="text-xs text-gray-400 mt-0.5">
            Saldo = stand van vandaag · opgenomen uren volgen het gekozen jaar
          </p>
        </div>
        <YearSelector year={year} onChange={setYear} />
      </div>

      {/* Content */}
      {showAll ? (
        <AllEmployeesCard year={year} />
      ) : employeeId ? (
        <SingleEmployeeCard employeeId={employeeId} year={year} />
      ) : (
        <div className="p-6 text-center">
          <p className="text-sm text-gray-500">Selecteer een medewerker om verlofsaldi te bekijken.</p>
        </div>
      )}
    </div>
  );
}
